import { Alert } from '@creditornot/cb-alert';
import { Button } from '@creditornot/cb-button';
import { colors } from '@creditornot/cb-ingredients';
import { ModalFooter } from '@creditornot/cb-modal';
import { cssThemedValue } from '@creditornot/cb-sc-utils';
import { InputSwitch, Switch } from '@creditornot/cb-switch';
import { CookieConsents } from '@creditornot/cwc-cookie-sdk';
import { FORM_ERROR } from 'final-form';
import React, { useCallback } from 'react';
import { Form, Field, useFormState } from 'react-final-form';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { typographyCss } from 'common/typography';

import InformationDisplay from './InformationDisplay';
import ResponsiveContainer from './ResponsiveContainer';
import getErrorContent from '../../../common/utils/error';

export type ManageCookieConsentsFormProps = {
  className?: string;
  onSubmit: (data: CookieConsents) => void;
  onSubmitFailed: (error: any) => void;
  onClose: () => void;
  initialValues: CookieConsents;
};

export type ManageCookieConsentsFormComponentProps = {
  className?: string;
};

const Text = styled.span`
  ${typographyCss.Body3}
  color: ${cssThemedValue({
    light: colors.pepper64,
    dark: colors.salt64,
  })};
`;

const Row = styled(ResponsiveContainer)`
  margin-block-start: 24px;
  gap: 12px;
  align-items: baseline;
  justify-content: space-between;
`;

const FormFooter = styled(ModalFooter)`
  margin-block-start: 24px;
`;

const Root = styled.div`
  overflow: auto;
`;

const RffSwitch = ({ name, inputId }: { name: string; inputId: string }) => {
  return (
    <Field<boolean> name={name}>
      {({ input }) => (
        <InputSwitch
          id={inputId}
          checked={input.value}
          onChange={() => {
            input.onChange(!input.value);
          }}
        />
      )}
    </Field>
  );
};

export const ManageCookieConsentsFormFields = ({ className }: ManageCookieConsentsFormComponentProps) => {
  const { submitFailed, submitError } = useFormState<CookieConsents>();

  return (
    <Root data-testid="manage-cookie-consent-form" className={className}>
      <Row>
        <InformationDisplay
          labelColor="black"
          label={
            <label htmlFor="required-input">
              <FormattedMessage id="gdpr-consents.manage.required.title" />
            </label>
          }
        >
          <Text>
            <FormattedMessage id="gdpr-consents.manage.required.description" />
          </Text>
        </InformationDisplay>

        <Switch name="required-input" checked readOnly disabled />
      </Row>

      <Row>
        <InformationDisplay
          labelColor="black"
          label={
            <label htmlFor="functional-input">
              <FormattedMessage id="gdpr-consents.manage.functional.title" />
            </label>
          }
        >
          <Text>
            <FormattedMessage id="gdpr-consents.manage.functional.description" />
          </Text>
        </InformationDisplay>

        <RffSwitch name="functional" inputId="functional-input" />
      </Row>

      <Row>
        <InformationDisplay
          labelColor="black"
          label={
            <label htmlFor="analytics-input">
              <FormattedMessage id="gdpr-consents.manage.analytics.title" />
            </label>
          }
        >
          <Text>
            <FormattedMessage id="gdpr-consents.manage.analytics.description" />
          </Text>
        </InformationDisplay>

        <RffSwitch name="analytics" inputId="analytics-input" />
      </Row>

      <Row>
        <InformationDisplay
          labelColor="black"
          label={
            <label htmlFor="marketing-input">
              <FormattedMessage id="gdpr-consents.manage.marketing.title" />
            </label>
          }
        >
          <Text>
            <FormattedMessage id="gdpr-consents.manage.marketing.description" />
          </Text>
        </InformationDisplay>

        <RffSwitch name="marketing" inputId="marketing-input" />
      </Row>

      {submitFailed && (
        <Row>
          <Alert variant="error" title="Error while submitting">
            {getErrorContent(submitError)}
          </Alert>
        </Row>
      )}

      <FormFooter>
        <Button variant="blue" size="large" stretch type="submit">
          <FormattedMessage id="gdpr-consents.manage.save-selections-button" />
        </Button>
      </FormFooter>
    </Root>
  );
};

export const ManageCookieConsentsForm = ({
  className,
  onSubmit,
  onSubmitFailed,
  onClose,
  initialValues,
}: ManageCookieConsentsFormProps) => {
  const handleSubmitFailed = useCallback(
    (error: any) => {
      onSubmitFailed(error);
      if (error instanceof Error) {
        return { [FORM_ERROR]: { message: error.message, data: error.stack } };
      } else {
        return { [FORM_ERROR]: { message: JSON.stringify(error) } };
      }
    },
    [onSubmitFailed],
  );

  const handleSubmitAndError = useCallback(
    (data: CookieConsents) => {
      try {
        // TODO this behavior means that the modal will be stuck open in case of errors.
        //   This could be problematic in case of a global problem with the modal itself.
        //   Users will not be able to use m-admin
        onSubmit(data);
        onClose();
        return {};
      } catch (error) {
        return handleSubmitFailed(error);
      }
    },
    [handleSubmitFailed, onSubmit, onClose],
  );

  return (
    <Form<CookieConsents> initialValues={initialValues} onSubmit={handleSubmitAndError}>
      {({ handleSubmit }) => (
        <form onSubmit={handleSubmit} className={className}>
          <ManageCookieConsentsFormFields />
        </form>
      )}
    </Form>
  );
};
